import axios from "axios";
import { z } from "zod";
import { environment } from "../../../environment";
import { AccountType, ClientId, CustodianAccountId, custodianOptions, ExternalAccountId, FartherAccountId, OperationalState, partners, Prefix, Suffix, } from "../../Types";
import ValidationError from "../../ZodError";
export { AccountType };
const Name = z.object({
    first: z.string(),
    last: z.string(),
    middle: z.string().nullable(),
    prefix: Prefix.nullable(),
    suffix: Suffix.nullable(),
});
const Custodian = z.enum(custodianOptions);
const AccountSubDetails = z.object({
    accountBalance: z.number().nullable(),
    accountType: AccountType,
    description: z.string().nullable(),
    displayName: z.string(),
    isSMA: z.boolean().optional(),
    operationalState: OperationalState,
    performanceStartDate: z.string().nullable(),
    reportingCurrencyCode: z.string(),
    smaManager: z.string().nullable(),
    smaStrategy: z.string().nullable(),
    targetDate: z.string().nullable(),
    targetGoal: z.number().nullable(),
    custodian: Custodian,
    custodianAccountNumber: CustodianAccountId,
});
const Advisor = z.object({
    advisorId: z.string(),
    almaMaters: z.string().nullable(),
    calendarUrl: z.string().nullable(),
    concentration: z.string().nullable(),
    description: z.string().nullable(),
    email: z.string().nullable(),
    headshotPath: z.string().nullable(),
    hometown: z.string().nullable(),
    linkedinUrl: z.string().nullable(),
    location: z.string().nullable(),
    name: Name,
});
// https://plaid.com/docs/api/accounts/#account-type-schema
const ExternalAccountType = z.enum([
    "credit",
    "investment",
    "depository",
    "loan",
    "other",
]);
export const ExternalConnectionHealth = z.enum([
    "Healthy",
    "Unavailable",
    "Degraded",
    "Down",
]);
const ExternalAccount = z.object({
    accountId: ExternalAccountId,
    accountType: ExternalAccountType,
    availableBalance: z.number().nullable(),
    connectionHealthType: ExternalConnectionHealth,
    currentBalance: z.number().nullable(),
    institutionName: z.string(),
    lastUpdated: z.string(),
    linkId: z.string(),
    mask: z.string().nullable(),
    name: z.string(),
});
const ExternalLink = z.object({
    institutionId: z.string(),
    institutionName: z.string(),
    linkId: z.string(),
    status: z.string(),
});
const JointHolder = z.object({ clientId: ClientId, name: Name });
const AccountDetails = z.object({
    accountDetails: AccountSubDetails,
    accountId: FartherAccountId,
    jointHolders: JointHolder.array().nullable(),
});
export const ApexClusterClientId = z.custom((val) => typeof val === "number");
const Partner = z.enum(partners);
const Response = z.object({
    accountDetails: AccountDetails.array(),
    advisor: Advisor,
    externalAccountDetails: z.object({
        accounts: ExternalAccount.array(),
        links: ExternalLink.array(),
    }),
    defaultCustodian: Custodian,
    apexClientId: ApexClusterClientId.nullable(),
    partner: Partner,
});
export const url = (clientId) => environment() === "PROD"
    ? `https://middleware.prod.farther.com/dashboard/client/${clientId}/dashboard`
    : `https://middleware.uat.farther.com/dashboard/client/${clientId}/dashboard`;
export default async function clientDashboard(clientId, config) {
    const currentURL = url(clientId);
    const { data } = await axios.get(currentURL, {
        headers: {
            Authorization: `Bearer ${config.jwt}`,
            Client: config.clientId,
            ...(config.advisorId && { Advisor: config.advisorId }),
        },
    });
    const decoded = Response.safeParse(data);
    if (decoded.success) {
        return decoded.data;
    }
    else {
        const errorMessage = "[clientDashboard] Could not parse";
        console.error(`${errorMessage}, ${decoded.error.message}`);
        throw new ValidationError(errorMessage, decoded.error.issues);
    }
}
//# sourceMappingURL=getClientDashboard.js.map